import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery } from '@tanstack/react-query';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Checkbox from 'components/shared/form/Checkbox';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { useProfile } from 'context/profile';
import type { ProductAccess as ProductAccessValues } from 'models/flexible/Client';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminShowClient, adminUpdateClient } from 'services/api/admin/clients';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

const PRODUCTS: { key: keyof ProductAccessValues; label: string }[] = [
  { key: 'traditionalOlio', label: 'Care Coordination (Traditional Olio)' },
  { key: 'olioSummarization', label: 'Summarization Workspace' },
  { key: 'referralSummarization', label: 'Referral Summarization' },
  { key: 'dischargeCalendar', label: 'Discharge Calendar' },
  { key: 'admissions', label: 'Admissions' },
  { key: 'inboundReferrals', label: 'Inbound Referrals' },
];

export default function ProductAccess() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const disabled = !profile.has(PERMISSIONS.adminClientEdit);

  const { data: client, isLoading } = useQuery({
    queryKey: ['adminClient', id, 'productAccess'],
    queryFn: ({ signal }) => adminShowClient(id!, signal),
    enabled: !!id,
  });

  const [values, setValues] = useState<ProductAccessValues | null>(null);
  const [serverValues, setServerValues] = useState<ProductAccessValues | null>(null);

  useEffect(() => {
    if (client?.productAccess) {
      const full: ProductAccessValues = {
        traditionalOlio: { enabled: client.productAccess.traditionalOlio?.enabled ?? false },
        olioSummarization: { enabled: client.productAccess.olioSummarization?.enabled ?? false },
        referralSummarization: { enabled: client.productAccess.referralSummarization?.enabled ?? false },
        dischargeCalendar: { enabled: client.productAccess.dischargeCalendar?.enabled ?? false },
        admissions: { enabled: client.productAccess.admissions?.enabled ?? false },
        inboundReferrals: { enabled: client.productAccess.inboundReferrals?.enabled ?? false },
      };
      setValues(full);
      setServerValues(full);
    }
  }, [client]);

  const isDirty = JSON.stringify(values) !== JSON.stringify(serverValues);

  const mutation = useMutation({
    mutationFn: (payload: Partial<ProductAccessValues>) => adminUpdateClient(id!, payload),
    onSuccess: () => {
      addToast({ text: 'Product access successfully updated' });
      navigate('/admin/clients');
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || 'An error occurred while updating product access';
      addToast({ text: msg });
    },
  });

  const handleToggle = (key: keyof ProductAccessValues) => {
    if (!values) return;
    setValues({ ...values, [key]: { enabled: !values[key].enabled } });
  };

  const handleSave = () => {
    if (!values) return;
    mutation.mutate(values);
  };

  const goBack = () => navigate('/admin/clients');

  if (isLoading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title='Product Access' subtitle={client?.name} />
      <StyledFormSection>
        {PRODUCTS.map(({ key, label }) => (
          <StyledCheckbox
            key={key}
            checked={values?.[key]?.enabled ?? false}
            onChange={() => handleToggle(key)}
            label={label}
            labelSize='14px'
            checkedColor={colors.primaryBlue}
            disabled={disabled}
            size={14}
          />
        ))}
      </StyledFormSection>
      <ButtonGroup style={{ justifyContent: 'flex-end', marginTop: 24 }}>
        <Button variant='ghost' onClick={goBack}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={disabled || !isDirty || mutation.isPending} loading={mutation.isPending}>
          Update Product Access
        </Button>
      </ButtonGroup>
    </AdminFormPage>
  );
}

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;

const StyledFormSection = styled(FormSection)`
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 16px;

  &:first-child {
    margin-top: 8px;
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;
